import { GUEST_PORTAL_URL, NEO_GP_URL } from '@/assets/configurations/Settings';
import { useAppFeatures } from '@/hooks/useAppFeatures';
import { logger } from '@/libs/logger';
import { ConfigType, RootSnackbarState } from '@/screens/root';
import { Welcome } from '@/screens/root/Welcome';
import { NotFound, Splash } from '@components';
import AppConfig from '@config';
import { SupportedLocale } from '@localizations';
import { VerificationStatus } from '@operto/verifications-shared';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { AppState, dispatchGetCompany, dispatchReset } from '@store';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DefaultTheme, Provider as PaperProvider } from 'react-native-paper';
import { useSelector } from 'react-redux';
import { useChekinStatus } from './chekin/hooks';
import { getReservationIdFromConfigId } from './chekin/utils';
import { useGetGuestUserQuery } from './services/userApi';

const MAX_RETRY_ATTEMPTS = 3;
const SCREENS_FOR_GENERIC_URL = ['auth', 'verify-otp', 'trips'];

const AppContext = React.createContext({
  companyId: '',
  locale: 'en' as SupportedLocale,
  setLocale: (_locale: SupportedLocale) => {
    // do nothing - implementation is defined in setLocale useState
  },
  chatToken: {},
  urlConfig: { id: '', entryPath: '' } as ConfigType,
  setUrlConfig: (_config: ConfigType) => {
    // do nothing - implementation is defined in setConfig useState
  },
  openSnackbar: (_state: RootSnackbarState) => {
    // do nothing - implementation is defined in setRootSnackbarState useState
  },
  isChekinRequired: false,
  chekinStatus: undefined as VerificationStatus | undefined,
  isChekinEnabled: false,
  isChekinError: false,
  isChekinLoading: false,
  refetchChekinStatus: () => {
    // do nothing - implementation is defined in useChekinVerification
  },
  startChekinStatusPolling: () => {
    // do nothing - implementation is defined in useChekinStatus
  },
});

export const useAppContext = () => React.useContext(AppContext);

type AppProviderProps = {
  config: {
    id: string;
    entryPath?: string;
    origin: string;
  };
  children: React.ReactNode;
  setConfig: (config: ConfigType) => void;
  openSnackbar: (state: RootSnackbarState) => void;
};

export const AppProvider = ({
  config,
  setConfig,
  children,
  openSnackbar,
}: AppProviderProps) => {
  const { isFeatureEnabled, isLoading: isLoadingAppFeatures } =
    useAppFeatures();
  const [companyId, setCompanyId] = useState('');
  const [locale, setLocale] = useState<SupportedLocale>('en');
  const [retryAttempts, setRetryAttempts] = useState(0);
  const [isLoadingCompany, setIsLoadingCompany] = useState(false);
  const { data: guest } = useGetGuestUserQuery();
  const { company, theme, chatToken } = useSelector(
    (state: { guestportal: AppState }) => {
      const appState = state.guestportal;
      const id = companyId.length ? companyId : config.id.split('_').shift();
      const tempCompany = appState.companies[id ?? ''];

      return {
        company: tempCompany,
        theme: appState.themes[tempCompany?.theme as string],
        chatToken: appState.chatToken,
      };
    },
  );

  const reservationId = useMemo(() => {
    const companyReservationId =
      typeof company?.reservation === 'string'
        ? company.reservation
        : company?.reservation?.id;

    return companyReservationId ?? getReservationIdFromConfigId(config.id);
  }, [company?.reservation, config.id]);

  const chekinContext = useChekinStatus({
    companyId: company?.id,
    reservationId,
  });

  // set the theme
  const appTheme = {
    ...DefaultTheme,
    roundness: AppConfig.Styles.borderRadius,
    colors: {
      ...DefaultTheme.colors,
      primary:
        theme?.enabled && (theme?.primaryColor?.length ?? 0) > 1
          ? theme?.primaryColor
          : AppConfig.Colors.PRIMARY,
      accent:
        theme?.enabled && (theme?.accentColor?.length ?? 0) > 1
          ? theme?.accentColor
          : AppConfig.Colors.ACCENT,
      background: AppConfig.Colors.ON_SECONDARY,
      card: AppConfig.Colors.ON_SECONDARY,
      primaryContainer: AppConfig.Colors.DARK_PRIMARY,
      tertiary: AppConfig.Colors.DARK_ON_PRIMARY,
    },
  };

  const renderEntryPath = () => {
    const currentHostname = window.location?.hostname;
    if (
      currentHostname &&
      !currentHostname.includes('localhost') &&
      !currentHostname.includes('guests.operto') &&
      !currentHostname.includes('guest.operto')
    ) {
      window.location.replace(`${GUEST_PORTAL_URL}/${config.id}/?r=true`);
    }

    if (
      SCREENS_FOR_GENERIC_URL.some(e => `/${e}` === config.entryPath) &&
      !isLoadingCompany
    ) {
      return children;
    }

    if (retryAttempts >= 3) {
      return <NotFound />;
    }

    if (
      config.id &&
      (companyId.length === 0 ||
        !company ||
        isLoadingCompany ||
        isLoadingAppFeatures)
    ) {
      return <Splash />;
    }

    if (config.entryPath === '/welcome') {
      return <Welcome />;
    }

    if (
      company &&
      !company.isTestReservation &&
      isFeatureEnabled('newGuestPortal', company.companyId)
    ) {
      let redirectPath = `${NEO_GP_URL}/${config.id}${config.entryPath}`;
      if (['/profile', '/orders'].includes(config.entryPath ?? '')) {
        redirectPath = `${NEO_GP_URL}${config.entryPath}`;
      }

      window.location.replace(redirectPath);
      return <Splash />;
    }

    return children;
  };

  const updateLocale = useCallback((newLocale: SupportedLocale) => {
    setLocale(newLocale);
  }, []);

  const appContextValue = useMemo(
    () => ({
      companyId,
      locale,
      setLocale: updateLocale,
      chatToken,
      urlConfig: config,
      setUrlConfig: setConfig,
      openSnackbar,
      ...chekinContext,
    }),
    [
      companyId,
      locale,
      updateLocale,
      chatToken,
      config,
      setConfig,
      openSnackbar,
      chekinContext,
    ],
  );

  useEffect(() => {
    const getLocaleSource = () => {
      AsyncStorage.getItem(`locale-${companyId}`).then(storedLocale => {
        const defaultLocale = 'en' as SupportedLocale;
        const companyLocale = company?.locale as SupportedLocale;
        const guestLocale = guest?.locale as SupportedLocale;
        let newLocale = companyLocale ?? defaultLocale;

        if (guestLocale) {
          newLocale = guestLocale;
          AsyncStorage.setItem(`locale-${companyId}`, guestLocale);
        } else if (storedLocale) {
          newLocale = storedLocale as SupportedLocale;
        }
        setLocale(newLocale);
      });
    };

    if (companyId.length === 0 && company) {
      setCompanyId(company.id);
    }

    if (companyId) {
      getLocaleSource();
    }
  }, [company, companyId, guest?.locale]);

  useEffect(() => {
    const getTheming = async () => {
      try {
        if (config.id.split('_').length > 1) {
          setIsLoadingCompany(true);
          await dispatchGetCompany(config.id); // config.id => {companyId}_{externalReservationId}
          setIsLoadingCompany(false);
        }
      } catch (error) {
        logger.error(error, retryAttempts);

        if (error && retryAttempts < MAX_RETRY_ATTEMPTS) {
          setRetryAttempts(retryAttempts + 1);
        }
      }
    };

    if (retryAttempts >= MAX_RETRY_ATTEMPTS) {
      dispatchReset();
    } else {
      if (config.id.length === 0) {
        return;
      }

      if (config.entryPath === '/welcome') {
        setCompanyId(config.id); // config.id => {companyId}
      } else if (company === undefined) {
        getTheming();
      }
    }
  }, [company, config, retryAttempts]);

  return (
    <>
      <PaperProvider theme={appTheme}>
        <AppContext.Provider value={appContextValue}>
          {renderEntryPath()}
        </AppContext.Provider>
      </PaperProvider>
    </>
  );
};
